import { useCallback } from 'react';

import { logError } from 'lib/sentry/logError';
import { MenuItem } from 'shared';

import {
  ChallengeTimelineItemType,
  IChallengeTimelineItem,
} from 'features/challenge/ChallengeTimelineItem';
import { useEditChallengesMutation } from 'features/challenge/hooks/mutation/useEditChallenges';
import { useOpenChallengeModal } from 'features/challenge/hooks/useOpenChallengeModal';
import { useGoalQueryActions } from 'features/goal/hooks/useGoalQueryActions';
import { IGoal } from 'features/goal/types';

type TGoalChallengeCardEditButton = {
  challenge: IChallengeTimelineItem;
  focusAreaId?: string;
};

type MutationContext = {
  challenges: IChallengeTimelineItem[];
  previousGoal: IGoal | undefined;
};

export const GoalChallengeCardEditButton = ({
  challenge,
  focusAreaId,
}: TGoalChallengeCardEditButton) => {
  const openChallengeModal = useOpenChallengeModal();

  const {
    cancelGoalQueries,
    getGoalQuery,
    updateGoalQueryData,
    invalidateGoalQuery,
  } = useGoalQueryActions();

  const { mutateAsync: editGoalBreakdownChallenge } =
    useEditChallengesMutation<IChallengeTimelineItem>({
      options: {
        onMutate: async ({ challenges }) => {
          cancelGoalQueries();
          const previousGoal = getGoalQuery();

          if (Array.isArray(challenges) && challenges.length === 1) {
            updateGoalQueryData((draft) => {
              if (focusAreaId) {
                const focusArea = draft.focusAreas.find(
                  (focusArea) => focusArea.focusAreaId === focusAreaId
                );

                if (focusArea) {
                  const challengeIndex = focusArea.challenges.findIndex(
                    (challenge) =>
                      challenge.challengeId === challenges[0].challengeId
                  );

                  if (challengeIndex !== -1) {
                    focusArea.challenges[challengeIndex] =
                      challenges[0] as ChallengeTimelineItemType;
                  }
                }
              } else {
                const challengeIndex = draft.challenges.findIndex(
                  (challenge) =>
                    challenge.challengeId === challenges[0].challengeId
                );

                if (challengeIndex !== -1) {
                  draft.challenges[challengeIndex] =
                    challenges[0] as ChallengeTimelineItemType;
                }
              }
            });
          }

          return { challenges, previousGoal } as MutationContext;
        },
        onSuccess: () => {
          invalidateGoalQuery();
        },
        onError: async (error, __, context) => {
          if (context?.challenges) {
            updateGoalQueryData((draft) => {
              draft.challenges =
                context.challenges as ChallengeTimelineItemType[];
            });
          }
          logError(error);
        },
      },
    });

  const onSubmit = useCallback(
    async (updatedChallenge: IChallengeTimelineItem) =>
      await editGoalBreakdownChallenge({
        challenges: [updatedChallenge],
      }),
    [editGoalBreakdownChallenge]
  );

  const onCreateGoalChallengeBtnClick = () =>
    openChallengeModal({
      challenge,
      onChallengeSubmit: onSubmit,
      title: 'Create Challenge',
      sprint: null,
    });

  return <MenuItem onClick={onCreateGoalChallengeBtnClick}>Edit</MenuItem>;
};
